import clsx from "clsx";

interface Option<T extends string> {
  value: T;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

interface RadioCardGroupProps<T extends string> {
  label: string;
  options: Option<T>[];
  value: T | undefined;
  onChange: (value: T) => void;
  error?: string;
  testId?: string;
}

export default function RadioCardGroup<T extends string>({
  label,
  options,
  value,
  onChange,
  error,
  testId,
}: RadioCardGroupProps<T>) {
  return (
    <fieldset className="mb-4">
      <legend className="text-sm font-medium text-neko-gray-light mb-2">{label}</legend>
      <div className="grid gap-2" role="radiogroup" data-testid={testId}>
        {options.map((opt) => (
          <label
            key={opt.value}
            className={clsx(
              "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all",
              value === opt.value
                ? "border-neko-orange bg-neko-orange/10"
                : "border-neko-dark-3 bg-neko-dark-2 hover:border-neko-gray"
            )}
          >
            <input
              type="radio"
              className="sr-only"
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
            />
            <div
              className={clsx(
                "w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 transition-all",
                value === opt.value
                  ? "border-neko-orange bg-neko-orange"
                  : "border-neko-gray"
              )}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {opt.icon && <span className="text-neko-orange">{opt.icon}</span>}
                <span className="text-sm font-medium text-neko-white">{opt.label}</span>
              </div>
              {opt.description && (
                <p className="text-xs text-neko-gray mt-0.5">{opt.description}</p>
              )}
            </div>
          </label>
        ))}
      </div>
      {error && <p className="text-xs text-neko-red mt-1" role="alert">{error}</p>}
    </fieldset>
  );
}
